import React, { useEffect, useRef, useState } from 'react';
import './AboutPage.css';
import Navbar from "./Components/Navbar";
import Footer from '../Components/Footer';
import PageHeroBanner from './Components/PageHeroBanner';
import Marquee from 'react-fast-marquee';
import { Leaf, Package, Globe, Heart, Sprout, Recycle, BookOpen, Lightbulb, Telescope } from 'lucide-react';
import { fetchSettings } from '../data/catalog';

const DEFAULT_FOUNDER_IMAGE = '/founder.jpeg';

function AboutPage() {
  const timelineRef = useRef(null);
  const headingRefs = useRef([]);
  const [founderImage, setFounderImage] = useState(DEFAULT_FOUNDER_IMAGE);

  // Use the admin-uploaded founder image if one is set, otherwise the default.
  useEffect(() => {
    let active = true;
    (async () => {
      const settings = await fetchSettings();
      if (active && settings && settings.founderImage) setFounderImage(settings.founderImage);
    })();
    return () => { active = false; };
  }, []);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let ticking = false;
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const timeline = timelineRef.current;
          if (!timeline) return;

          const timelineRect = timeline.getBoundingClientRect();
          const timelineTop = timelineRect.top;
          const timelineHeight = timelineRect.height;
          const windowHeight = window.innerHeight;
          const currentScrollY = window.scrollY;
          const scrollDirection = currentScrollY > lastScrollY ? 'down' : 'up';

          // Calculate progress based on scroll position with improved precision
          const scrollProgress = (windowHeight - timelineTop) / (windowHeight + timelineHeight);
          const progress = Math.max(0, Math.min(1, scrollProgress));
          
          // Update the sliding line with smooth movement
          const slidingLine = timeline.querySelector('.sliding-line');
          if (slidingLine) {
            slidingLine.style.height = `${progress * 100}%`;
          }

          // Timeline items are now visible by default - no animation needed
          // Removed the animation logic that was adding 'animate' class

          lastScrollY = currentScrollY;
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial call

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  return (
    <>
      <Navbar/>
      <div className="about-hero-wrap" style={{ marginTop: '5rem' }}>
        <PageHeroBanner
          heading="Our Story"
          image="/bgimage.jpeg"
          eyebrow="Ancient Wisdom · Modern Wellness"
        />
      </div>
      <div className="about-page-main-container">
        <div className="about-page">
          {/* Hero Header */}
          <div className="custom-header">
            <div className="custom-header-card">
              <h2 className="custom-header-title">Why Elaichi and Love?</h2>
              <p className="custom-subheading">
                Elaichi &amp; Love is where ancient wisdom meets modern wellness. From my mother&apos;s kitchen in Mumbai to the streets of Glasgow, every cup carries generations of love, healing, and connection.
              </p>
              <p className="custom-subheading">
                Elaichi is cardamom - the essence of home. Love is what we pour into everything we create. Together: a revolution in a cup.
              </p>
            </div>
            <div className="custom-header-image">
              <div className="founder-card">
                <img src={founderImage} alt="Rafia Taskeen, Founder of Elaichi & Love" className="founder-img" onError={e => { e.target.src = DEFAULT_FOUNDER_IMAGE; }} />
                <div className="founder-caption">
                  <span className="founder-name">Rafia Taskeen</span>
                  <span className="founder-role">Founder · CEO</span>
                </div>
              </div>
            </div>
          </div>

          <div className="timeline-section" ref={timelineRef}>
            <div className="timeline-container">
              <div className="timeline-line">
                <div className="sliding-line"></div>
              </div>

              <div className="timeline-item">
                <div className="timeline-content">
                  <div className="timeline-image">
                    <picture>
                      <source media="(max-width: 768px)" srcSet="/about1mobile.jpeg" />
                      <img src="/story-journey.jpeg" alt="From Mumbai to Glasgow - the journey of Elaichi & Love" />
                    </picture>
                  </div>
                  <div className="timeline-text">
                    <h3 className="timeline-heading"><span className="timeline-heading-icon"><BookOpen size={20} strokeWidth={1.8} /></span>The Story Behind Elaichi &amp; Love</h3>
                    <p>Founded by Chef Rashi Kedia, it started in Mumbai - in a kitchen where chai wasn’t just a drink, it was a ritual. My mother’s hands. Cardamom cracking. Steam rising. Elaichi & Love is more than a brand - it’s her vision of modern indulgence.</p>
                    <p>From Mumbai to Glasgow, we’re building three things: wellness chai that heals and connects; a Circular Kitchen Lab - food with purpose; and Chai Pe Charcha - open conversations and community through chai and events.</p>
                  </div>
                </div>
              </div>

              <div className="timeline-item">
                <div className="timeline-content">
                  <div className="timeline-text">
                    <h3 className="timeline-heading"><span className="timeline-heading-icon"><Lightbulb size={20} strokeWidth={1.8} /></span>Our Philosophy</h3>
                    <p>Quality without compromise: real spices, jaggery, black tea, gut-loving ingredients, and plastic-free packaging where we can. Every chai ball, snack, and ritual is designed to nourish and connect.</p>
                    <p>We believe the best ideas are born over a warm cup. Community events, Iftars, festivals, wellness workshops, and corporate experiences - all rooted in soulful sips and proper food.</p>
                  </div>
                  <div className="timeline-image">
                    <picture>
                      <source media="(max-width: 768px)" srcSet="/about2mobile.jpeg" />
                      <img src="/story-offerings.jpeg" alt="This isn't just chai - our offerings and experiences" />
                    </picture>
                  </div>
                </div>
              </div>

              <div className="timeline-item">
                <div className="timeline-content">
                  <div className="timeline-image">
                    <picture>
                      <source media="(max-width: 768px)" srcSet="/about3mobile.jpeg" />
                      <img src="/story-vision.jpeg" alt="With 25+ signature wellness offerings - we're building three things" />
                    </picture>
                  </div>
                  <div className="timeline-text">
                    <h3 className="timeline-heading"><span className="timeline-heading-icon"><Telescope size={20} strokeWidth={1.8} /></span>Our Vision</h3>
                    <p>Our vision is to redefine what indulgence means in the modern world - UK’s first food revolution - for the people, by the people. Promote local. Impact global. Stay rooted. With 25+ signature wellness offerings.</p>
                    <p>At Elaichi & Love, we don’t just make chai; we build wellness that heals, a Circular Kitchen Lab with purpose, and Chai Pe Charcha - solving problems of our community through chai, events, and love.</p>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Green Values Ribbon */}
          <div className="greenValuesRibbon">
            <Marquee speed={60} gradient={false}>
              {[
                { Icon: Recycle,  label: 'Zero Waste Kitchen' },
                { Icon: Package,  label: 'Plastic-Free Packaging' },
                { Icon: Globe,    label: 'Local Sourcing' },
                { Icon: Heart,    label: 'Community First' },
                { Icon: Sprout,   label: 'Ayurvedic Roots' },
                { Icon: Recycle,  label: 'Zero Waste Kitchen' },
                { Icon: Package,  label: 'Plastic-Free Packaging' },
                { Icon: Globe,    label: 'Local Sourcing' },
                { Icon: Heart,    label: 'Community First' },
                { Icon: Sprout,   label: 'Ayurvedic Roots' },
              ].map(({ Icon, label }, i) => (
                <span key={i} className="greenValuesItem">
                  <Icon size={18} strokeWidth={2} className="greenValuesIcon" />
                  <span className="greenValuesLabel">{label}</span>
                  <span className="greenValuesDot">·</span>
                </span>
              ))}
            </Marquee>
          </div>

          {/* Sustainability Section */}
          <div className="sustainSection">
            <div className="sustainBg" />
            <div className="sustainInner">
              <div className="sustainTextCol">
                <p className="sustainLabel">OUR CIRCULAR KITCHEN LAB</p>
                <h2 className="sustainTitle">Sustainability is not an add-on.<br/>It's our foundation.</h2>
                <p className="sustainBody">
                  We believe every food business has a responsibility to the planet. Our Circular Kitchen Lab is built on zero-waste principles - local sourcing, plastic-free packaging, and surplus food repurposed into nourishment rather than landfill.
                </p>
                <p className="sustainBody">
                  From the spices we choose to the packaging we refuse, every decision is guided by one question: <em>is this good for people and planet?</em>
                </p>
                <div className="sustainPillsRow">
                  <span className="sustainPill"><Leaf size={14} strokeWidth={2} /> Zero Waste Kitchen</span>
                  <span className="sustainPill"><Package size={14} strokeWidth={2} /> Plastic-Free Packaging</span>
                  <span className="sustainPill"><Globe size={14} strokeWidth={2} /> Local Sourcing</span>
                  <span className="sustainPill"><Sprout size={14} strokeWidth={2} /> Surplus Repurposed</span>
                </div>
              </div>
              <div className="sustainStatsCol">
                <div className="sustainStat">
                  <span className="sustainStatNum">0%</span>
                  <span className="sustainStatLabel">Artificial Additives</span>
                </div>
                <div className="sustainStat">
                  <span className="sustainStatNum">100%</span>
                  <span className="sustainStatLabel">Real Ingredients</span>
                </div>
                <div className="sustainStat">
                  <span className="sustainStatNum"><Recycle size={32} strokeWidth={1.8} /></span>
                  <span className="sustainStatLabel">Circular Food Model</span>
                </div>
                <div className="sustainStat">
                  <span className="sustainStatNum"><Globe size={32} strokeWidth={1.8} /></span>
                  <span className="sustainStatLabel">Planet-First Decisions</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <Footer />
    </>
  );
}

export default AboutPage;